import { useState, useEffect } from 'react';
import { ArrowLeft, Edit, Eye, Users, CheckCircle, Heart, MapPin, Briefcase, GraduationCap, Phone, Mail, Shield, Camera } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Progress } from './ui/progress';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchProfile } from '../store/slices/profileSlice';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { ImageWithFallback } from './figma/ImageWithFallback';
import axios from '../utils/axios.config';
import { formatMaritalStatus, formatHeight } from '../utils/profileFormatters';

interface MyProfilePageProps {
  onBack: () => void;
  onEdit?: () => void;
}

export default function MyProfilePage({ onBack, onEdit }: MyProfilePageProps) {
  const dispatch = useAppDispatch();
  const { data: profile, loading } = useAppSelector(state => state.profile);
  const { user } = useAppSelector(state => state.auth);
  const [stats, setStats] = useState({ views: 0, interests: 0, matches: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  const {
    getProfilePhoto,
    getFullName,
    getInitials,
    getLocation,
    getAge,
    getHeight,
    getEducation,
    getOccupation,
    getBio,
    getReligion,
    getMaritalStatus,
    getProfileCompletionPercentage
  } = useUserDisplay();

  useEffect(() => {
    if (!profile) {
      dispatch(fetchProfile());
    }
  }, [dispatch, profile]);

  // Fetch profile stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Try profile analytics endpoint first (profile views)
        const analyticsResponse = await axios.get('/profile-analytics/stats');

        // Try interactions endpoint for interests/matches
        const interactionsResponse = await axios.get('/interactions/stats');

        if (analyticsResponse.data.success || interactionsResponse.data.success) {
          const analyticsData = analyticsResponse.data.data || {};
          const interactionsData = interactionsResponse.data.data || {};

          setStats({
            views: analyticsData.totalViews || 0,
            interests: interactionsData.receivedCount || interactionsData.interestsReceived || 0,
            matches: interactionsData.matchesCount || interactionsData.totalMatches || 0
          });
        }
      } catch (error) {
        // Silent fail - keep default values
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const completionPercentage = getProfileCompletionPercentage();
  const profilePhoto = getProfilePhoto();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-md flex-shrink-0">
        <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={onBack}>
          <ArrowLeft className="size-6" />
        </Button>
        <span className="text-lg font-medium">My Profile</span>
        <Button
          variant="ghost"
          size="icon"
          className="text-white hover:bg-white/20"
          onClick={onEdit}
        >
          <Edit className="size-6" />
        </Button>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="max-w-7xl mx-auto md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left Column: Photo & Basic Info */}
            <div className="md:col-span-1 space-y-6">
              {/* Profile Card */}
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                <div className="h-32 bg-gradient-to-r from-red-100 to-orange-100 relative">
                  <button
                    className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-red-600 backdrop-blur-sm transition-colors"
                    onClick={onEdit}
                  >
                    <Edit className="size-4" />
                  </button>
                </div>
                <div className="px-6 pb-6 relative">
                  <div className="relative -mt-16 mb-4 inline-block">
                    {profilePhoto ? (
                      <>
                        <ImageWithFallback
                          src={profilePhoto}
                          alt={getFullName()}
                          className="size-32 rounded-full border-4 border-white object-cover shadow-md"
                        />
                        {/* Check for pending status on primary photo */}
                        {profile?.photos?.find(p => p.url === profilePhoto && (p.status === 'pending' || p.isApproved === false)) && (
                          <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center backdrop-blur-[1px] border-4 border-white">
                            <span className="bg-yellow-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                              Pending
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <Avatar className="size-32 border-4 border-white shadow-md">
                        <AvatarFallback className="bg-gradient-to-r from-red-600 to-orange-600 text-white text-3xl">
                          {getInitials()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <button
                      className="absolute bottom-0 right-0 size-10 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 border-4 border-white shadow-sm transition-colors z-10"
                      onClick={onEdit}
                    >
                      <Camera className="size-5" />
                    </button>
                  </div>

                  <div className="mb-6">
                    <h1 className="text-2xl font-bold mb-1">
                      {getFullName()}
                    </h1>
                    <p className="text-gray-500 text-sm mb-3">ID: {user?._id?.slice(-8).toUpperCase()}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {user?.isPremium && (
                        <Badge className="bg-red-600 hover:bg-red-600 shadow-sm">Pro Member</Badge>
                      )}
                      {user?.isVerified && (
                        <Badge className="bg-green-500 hover:bg-green-500 flex items-center gap-1 shadow-sm">
                          <CheckCircle className="size-3" />
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Profile Completion */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">Profile Completeness</span>
                      <span className="text-red-600 font-bold">{completionPercentage}%</span>
                    </div>
                    <Progress value={completionPercentage} className="h-2 mb-3" />
                    <p className="text-xs text-gray-500 mb-3">
                      {completionPercentage === 100
                        ? 'Your profile is complete!'
                        : 'Complete your profile to get 10x more responses!'}
                    </p>
                    {completionPercentage < 100 && (
                      <Button
                        size="sm"
                        className="w-full bg-red-600 hover:bg-red-700 text-xs"
                        onClick={onEdit}
                      >
                        Complete Profile
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats - Visible on Desktop */}
              <div className="hidden md:grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
                  <Eye className="size-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold mb-1">{statsLoading ? '...' : stats.views}</p>
                  <p className="text-xs text-gray-500">Views</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
                  <Users className="size-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold mb-1">{statsLoading ? '...' : stats.interests}</p>
                  <p className="text-xs text-gray-500">Interests</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
                  <CheckCircle className="size-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold mb-1">{statsLoading ? '...' : stats.matches}</p>
                  <p className="text-xs text-gray-500">Matches</p>
                </div>
              </div>
            </div>

            {/* Right Column: Details */}
            <div className="md:col-span-2 space-y-6 p-4 md:p-0">
              {/* Mobile Stats (Hidden on Desktop) */}
              <div className="grid grid-cols-3 gap-4 md:hidden mb-6">
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
                  <Eye className="size-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold mb-1">{statsLoading ? '...' : stats.views}</p>
                  <p className="text-xs text-gray-500">Views</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
                  <Users className="size-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold mb-1">{statsLoading ? '...' : stats.interests}</p>
                  <p className="text-xs text-gray-500">Interests</p>
                </div>
                <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
                  <CheckCircle className="size-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold mb-1">{statsLoading ? '...' : stats.matches}</p>
                  <p className="text-xs text-gray-500">Matches</p>
                </div>
              </div>

              {/* About */}
              {profile?.about && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Heart className="size-5 text-red-600" />
                    About Me
                  </h2>
                  <p className="text-gray-700 leading-relaxed">{profile.about}</p>
                </div>
              )}

              {/* Basic Details */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Users className="size-5 text-red-600" />
                  Basic Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Age</p>
                      <p className="font-medium">{profile?.dateOfBirth ? new Date().getFullYear() - new Date(profile.dateOfBirth).getFullYear() : 'N/A'} years</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Height</p>
                      <p className="font-medium">{profile?.height ? formatHeight(profile.height) : 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Religion</p>
                      <p className="font-medium">{profile?.religion || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Community</p>
                      <p className="font-medium">{profile?.community || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Marital Status</p>
                      <p className="font-medium">{formatMaritalStatus(profile?.maritalStatus) || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <MapPin className="size-5 text-red-600" />
                  Location
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">City</p>
                      <p className="font-medium">{profile?.city || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">State</p>
                      <p className="font-medium">{profile?.state || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Country</p>
                      <p className="font-medium">{profile?.country || 'India'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Details */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Briefcase className="size-5 text-red-600" />
                  Professional Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Occupation</p>
                      <p className="font-medium">{profile?.occupation || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Work Location</p>
                      <p className="font-medium">{profile?.workLocation || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Annual Income</p>
                      <p className="font-medium">{profile?.annualIncome ? `₹${profile.annualIncome}` : 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Education */}
              {profile?.education && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <GraduationCap className="size-5 text-red-600" />
                    Education
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Education Level</p>
                        <p className="font-medium">{profile.education.level || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Field of Study</p>
                        <p className="font-medium">{profile.education.field || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg sm:col-span-2">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Institution</p>
                        <p className="font-medium">{profile.education.institution || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Phone className="size-5 text-red-600" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="size-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Phone</p>
                      <p className="font-medium">{user?.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="size-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="font-medium text-sm break-all">{user?.email || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy & Security */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Shield className="size-5 text-red-600" />
                  Privacy & Security
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Profile Visibility</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Public</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Contact Privacy</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Premium Only</Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}